/**
 * HSE / safety full-job roll-up — the whole-well HSE view (§full-job): the live
 * LTI counters as header KPIs + a table of every day's safety header across the
 * job. Server-assembled by Oxbo-P (/wells/{uwi}/full-job/hse); the FE only
 * formats and lays it out.
 */
import { useLocale } from "@hooks/useLocale";
import { QueryState } from "@components/ui/QueryState";
import { StatBand, type Stat } from "@components/ui/StatBand";
import { fmtNum } from "@lib/format";
import { useGetFullJobHseQuery, type SafetyRowOut } from "./hseApi";
import type { Locale } from "@i18n/locales";

/** Locale-aware date for a HSE row (date_report is a plain YYYY-MM-DD). */
function fmtDate(iso: string | null, locale: Locale): string {
  if (!iso) return "—";
  const d = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString(locale, { year: "numeric", month: "short", day: "numeric" });
}

/** A pressure served in psi; null renders the em-dash. */
function fmtPressure(v: number | null, locale: Locale, psi: string): string {
  if (v == null || !Number.isFinite(v)) return "—";
  return `${fmtNum(v, locale, { maximumFractionDigits: 0 })} ${psi}`;
}

export function HseFullJob({ wellUwi }: { wellUwi: string }) {
  const { t, locale } = useLocale();
  const { data, isLoading, isError, refetch } = useGetFullJobHseQuery(wellUwi, {
    skip: !wellUwi,
  });

  const items: SafetyRowOut[] = data?.items ?? [];

  const yesNo = (v: boolean | null): string =>
    v == null ? "—" : v ? t("hse.inspections.yes") : t("hse.inspections.no");

  const stats: Stat[] = data
    ? [
        {
          label: t("hse.kpi.ltiOperator"),
          value: fmtNum(data.days_since_lti_operator, locale),
          tone: "good",
        },
        {
          label: t("hse.kpi.ltiTotal"),
          value: fmtNum(data.days_since_lti_total, locale),
          tone: "good",
        },
        { label: t("hse.fulljob.days"), value: fmtNum(items.length, locale) },
      ]
    : [];

  return (
    <QueryState
      isLoading={isLoading}
      isError={isError}
      isEmpty={!!data && items.length === 0}
      emptyMessage={t("hse.fulljob.noData")}
      onRetry={refetch}
    >
      <div className="page-head" style={{ marginBottom: 12 }}>
        <p className="subtitle">{t("hse.fulljob.subtitle")}</p>
      </div>
      {stats.length ? <StatBand stats={stats} /> : null}

      <div className="card" style={{ marginTop: 14 }}>
        <h3>{t("hse.fulljob.trail")}</h3>
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th>{t("hse.trail.date")}</th>
                <th className="num">{t("hse.trail.ltiOperator")}</th>
                <th className="num">{t("hse.trail.ltiTotal")}</th>
                <th className="num">{t("hse.trail.ridCards")}</th>
                <th className="num">{t("hse.trail.jsa")}</th>
                <th className="num">{t("hse.trail.jobsiteEval")}</th>
                <th className="num">{t("hse.trail.bopAnnular")}</th>
                <th className="num">{t("hse.trail.casingTest")}</th>
                <th>{t("hse.trail.company")}</th>
                <th>{t("hse.trail.government")}</th>
              </tr>
            </thead>
            <tbody>
              {items.map((s) => (
                <tr key={s.id} data-owner="p">
                  <td style={{ whiteSpace: "nowrap" }}>{fmtDate(s.date_report, locale)}</td>
                  <td className="num tab-nums">{fmtNum(s.days_since_lti_operator, locale)}</td>
                  <td className="num tab-nums">{fmtNum(s.days_since_lti_total, locale)}</td>
                  <td className="num tab-nums">{fmtNum(s.rid_cards, locale)}</td>
                  <td className="num tab-nums">{fmtNum(s.job_safety_analysis, locale)}</td>
                  <td className="num tab-nums">{fmtNum(s.jobsite_evaluation, locale)}</td>
                  <td className="num tab-nums">
                    {fmtPressure(s.bop_pressure_annular, locale, t("hse.unit.psi"))}
                  </td>
                  <td className="num tab-nums">
                    {fmtPressure(s.last_casing_test_pressure, locale, t("hse.unit.psi"))}
                  </td>
                  <td>{yesNo(s.is_company_inspected)}</td>
                  <td>{yesNo(s.is_government_inspected)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </QueryState>
  );
}
